import { StackIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { persistImageTransform, persistImageTransformBlob } from './imageTransformPersistence';

import {
  ImageStudioDialog,
  type ImageStudioDialogContext,
  type ImageStudioDialogMeta,
} from '@/components/create/v2/sections/image-studio/ImageStudioDialog';
import { ConnectShopifySheet } from '@/components/create/v2/sections/media-studio/ConnectShopifySheet';
import { type SuggestionChip } from '@/components/create/v2/sections/media-studio/SuggestionChips';
import { useMediaStudioOperation } from '@/components/create/v2/sections/media-studio/useMediaStudioOperation';
import { ConfirmCostDialog } from '@/components/ui/inspector/bodies/post/ConfirmCostDialog';
import type { InspectorBreadcrumbItem } from '@/components/ui/inspector/types';
import { useAuth } from '@/hooks/use-auth';
import { useAssetReuseInfo } from '@/hooks/useAssetReuseInfo';
import { useManualPostDraft } from '@/hooks/useManualPostDraft';
import { friendlyModelLabel } from '@/lib/ai-agent/model-label';
import { trackProductEvent } from '@/lib/analytics/product-events';
import { friendlyMessageFromError } from '@/lib/errors/friendly-errors';
import { requestCompositedUrl } from '@/lib/media-studio/request-composite';
import { routeForSource } from '@/lib/media-studio/route-for-source';
import { ITEM_META } from '@/lib/ui-tokens';
import { mediaService } from '@/services/media/mediaService';
import { useChatHandoffStore } from '@/stores/useChatHandoffStore';
import { useImageDialogStore } from '@/stores/useImageDialogStore';
import { downloadBlob } from '@/utils/downloadBlob';

function seedToString(seed: string | number | undefined): string {
  if (seed === undefined || seed === null || seed === '') return '—';
  return String(seed);
}

const COMMON_ASPECTS: Array<{ ratio: number; label: string }> = [
  { ratio: 1, label: '1:1' },
  { ratio: 16 / 9, label: '16:9' },
  { ratio: 9 / 16, label: '9:16' },
  { ratio: 4 / 5, label: '4:5' },
  { ratio: 5 / 4, label: '5:4' },
  { ratio: 4 / 3, label: '4:3' },
  { ratio: 3 / 4, label: '3:4' },
  { ratio: 3 / 2, label: '3:2' },
  { ratio: 2 / 3, label: '2:3' },
];

function computeAspectFromDims(w: number, h: number): string {
  if (!w || !h) return '1:1';
  const ratio = w / h;
  let best = COMMON_ASPECTS[0];
  let bestDelta = Math.abs(best.ratio - ratio);
  for (const candidate of COMMON_ASPECTS) {
    const delta = Math.abs(candidate.ratio - ratio);
    if (delta < bestDelta) {
      best = candidate;
      bestDelta = delta;
    }
  }
  // If no common ratio is within 5% of the actual, fall through to the precise
  // form so the user sees real numbers instead of a misleading approximation.
  if (bestDelta / ratio > 0.05) {
    const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
    const g = gcd(w, h);
    return `${Math.round(w / g)}:${Math.round(h / g)}`;
  }
  return best.label;
}

function deriveTitle(prompt: string | undefined, fallback = 'Untitled image'): string {
  if (!prompt) return fallback;
  const cleaned = prompt.trim().replace(/\s+/g, ' ');
  if (cleaned.length <= 80) return cleaned;
  return `${cleaned.slice(0, 77)}...`;
}

/**
 * Title for the header, clamped. `payload.title` used to win outright and is
 * unbounded — generated assets are stored with the FULL generation prompt as
 * their title, so the inspector header rendered a paragraph across the entire
 * window (live 2026-07-26: "Vertical 9:16 portrait of an athletic tennis player
 * holding the tennis racket featured in the product reference image while…").
 * deriveTitle already clamped the prompt fallback; the stored title simply
 * bypassed it. Route BOTH through the same clamp so the header can never grow
 * unbounded, whatever a caller stored.
 */
function resolveDisplayTitle(payload: { title?: string; prompt?: string }): string {
  return deriveTitle(payload.title?.trim() || payload.prompt);
}

interface ImageBodyProps {
  mediaId: string;
  sourcePostId?: string;
  onClose: () => void;
  breadcrumbs?: InspectorBreadcrumbItem[];
}

function toMediaBreadcrumb(breadcrumbs: InspectorBreadcrumbItem[] | undefined, leafLabel: string) {
  const root = breadcrumbs?.[0];
  if (!root) return undefined;
  const leaf = breadcrumbs[breadcrumbs.length - 1]?.label || leafLabel;
  return { rootLabel: root.label, rootHref: root.href, leafLabel: leaf };
}

export const ImageBody: React.FC<ImageBodyProps> = ({ onClose, breadcrumbs }) => {
  const payload = useImageDialogStore((s) => s.payload);
  const canUndo = useImageDialogStore((s) => s.history.length > 0);
  const canRedo = useImageDialogStore((s) => s.future.length > 0);
  const { createManualPostDraft, isCreatingManualPost } = useManualPostDraft();

  const handleCreateWithMora = () => {
    if (!payload?.imageUrl) return;
    useImageDialogStore.getState().close();
    navigate('/', {
      state: {
        imageReference: {
          imageUrl: payload.imageUrl,
          name: payload.title || deriveTitle(payload.prompt),
          prefill: 'Create a post using this image.',
        },
      },
    });
  };

  const handleCreatePost = () => {
    if (!payload?.imageUrl || isCreatingManualPost) return;
    void createManualPostDraft({
      mediaUrls: [payload.imageUrl],
      title: payload.title || deriveTitle(payload.prompt),
      mediaType: 'image',
    })
      .then(() => useImageDialogStore.getState().close())
      .catch(() => {
        // useManualPostDraft shows the recovery toast. Keep the asset open so
        // the user can retry instead of losing context after a failed draft.
      });
  };

  // Keyboard shortcuts: ⌘Z / Ctrl+Z to undo, ⌘⇧Z / Ctrl+⇧Z to redo. Bound
  // to the document while the image dialog is mounted. Skips when the
  // event target is an input/textarea/contenteditable so the user can still
  // undo their own typing in the prompt composer.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key !== 'z' && e.key !== 'Z') return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      if (e.shiftKey) {
        redoRef.current();
      } else {
        undoRef.current();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);
  const { session } = useAuth();
  const op = useMediaStudioOperation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isHandingOffRef = useRef(false);
  // True once a destructive transform (crop/rotate/flip) has repointed this
  // asset's media row at a new file — undo/redo must then keep the row in
  // sync with whatever the dialog is showing (editor-QA P0, 2026-07-03).
  const transformPersistedRef = useRef(false);
  // Keyboard-shortcut effect has an empty dep array; route through refs so it
  // always calls the freshest undo/redo closures.
  const undoRef = useRef<() => void>(() => {});
  const redoRef = useRef<() => void>(() => {});
  const [shopifySheetOrigin, setShopifySheetOrigin] = useState<
    'image-chip' | 'image-action-bar' | null
  >(null);
  // §6.6 confirm-cost gate: a "Animate" tap is ~$1.60 for a 4s clip
  // (Veo ~$0.40/sec) — never fire it one-tap. We stash the prepared run and
  // execute it only on explicit confirm.
  const [confirmAnimateOpen, setConfirmAnimateOpen] = useState(false);
  const pendingAnimateRef = useRef<(() => Promise<void>) | null>(null);
  // Asset-reuse context (§ Task 2, 2026-08-15): `overlay_layers` lives on the
  // shared media ROW, not per-post, so text added here bakes into every post
  // reusing this asset at publish time. Called unconditionally, before the
  // `!payload` early return, per Rules of Hooks — `payload?.assetId` is
  // undefined pre-load and the hook no-ops until it resolves.
  const reuseInfo = useAssetReuseInfo(payload?.assetId);

  if (!payload) return null;

  // The model line shows only a model Mora recorded and can name. Internal
  // pipeline ids and missing values leave it blank, and the dialog hides the row.
  const model = friendlyModelLabel(payload.model) ?? '';
  // D20: prefer the actual aspect computed from the image's natural dimensions
  // when available — payload.aspectRatio is sometimes stale (e.g. defaults to
  // '1:1' for variations whose source ratio differs). Falls back to the cached
  // payload.aspectRatio, then '1:1'.
  const aspect = (() => {
    if (payload.width && payload.height) {
      return computeAspectFromDims(payload.width, payload.height);
    }
    return payload.aspectRatio || '1:1';
  })();
  const dimensions =
    payload.width && payload.height ? `${payload.width}×${payload.height}` : undefined;

  const meta: ImageStudioDialogMeta = {
    model,
    aspect,
    seed: seedToString(payload.seed),
    dimensions,
  };

  const context: ImageStudioDialogContext = {
    assetId: payload.assetId,
    imageUrl: payload.imageUrl,
    prompt: payload.prompt,
    model,
    aspectRatio: aspect,
  };

  /**
   * Flatten-on-download.
   *
   * This used to composite in the browser with a Canvas 2D pass, which was the
   * ONLY place `media.overlay_layers` ever became pixels — the publish path
   * composited nothing, so a previewed headline shipped as a bare photo
   * (fixed 2026-08-12). Rather than keep two renderers that would drift, both
   * download and publish now call `/api/media/composite-overlays`.
   *
   * No overlay ⇒ the route returns the source URL unchanged, so this is still
   * a zero-cost direct download for the overwhelming majority of assets.
   *
   * Falling back to the raw image on failure is deliberate HERE and only here:
   * the user is looking at the file they get. The publish path treats the same
   * failure as fatal, because there nobody is looking.
   */
  const handleDownload = async (): Promise<void> => {
    const toastId = `download-${payload.assetId}`;
    try {
      const layers = await mediaService.getOverlayLayers(payload.assetId);
      if (layers.length === 0) {
        void downloadBlob(payload.imageUrl, `mora-image-${payload.assetId}.png`);
        return;
      }
      toast.loading('Flattening layers…', { id: toastId });
      const { url, composited } = await requestCompositedUrl(payload.imageUrl);
      await downloadBlob(url, `mora-image-${payload.assetId}.${composited ? 'jpg' : 'png'}`);
      if (composited) toast.success('Downloaded with text overlay.', { id: toastId });
      else toast.dismiss(toastId);
    } catch (err) {
      toast.dismiss(toastId);
      void downloadBlob(payload.imageUrl, `mora-image-${payload.assetId}.png`);
      if (import.meta.env.DEV) console.error('[ImageBody] flatten download failed:', err);
    }
  };

  /**
   * Generative-action close-and-route helper (PD-6 T2.3).
   *
   * Pushes a chat handoff onto useChatHandoffStore, closes the dialog, and
   * navigates to the route resolved by `routeForSource()`. The `_mode` arg
   * is currently advisory metadata; the agent infers the same mode from the
   * attachedImage + prompt content. Kept as a parameter for future telemetry.
   *
   * Guards:
   *  - Auth: returns false if not signed in (caller keeps dialog open).
   *  - Brand-kit: rejected as out-of-scope for PD-6 v1.
   *  - Single-flight: `isHandingOffRef` rejects rapid re-clicks; `op.run`
   *    wraps the close+navigate so MediaStudioOperation's lock fires for ≥1
   *    tick. routeForSource is the SINGLE source of truth for sessionId —
   *    no uuid fallback here (BLOCKER 2/3).
   */
  const handoffToChat = (
    prompt: string,
    _mode: 'inpaint' | 'background-replace' | 'variations' | 'caption',
  ): boolean => {
    if (isHandingOffRef.current) return false;
    if (!session?.access_token) {
      toast.error('Please sign in to continue.');
      return false;
    }
    const source = payload.source ?? { kind: 'chat' as const, sessionId: 'unknown' };
    if (source.kind === 'brand-kit') {
      toast.error('Brand-kit edits not supported yet.');
      return false;
    }
    isHandingOffRef.current = true;
    void op.run(async () => {
      try {
        const { route, sessionId } = routeForSource(source);
        useChatHandoffStore.getState().push({
          sessionId,
          parts: [
            { type: 'file', mediaType: 'image/png', url: payload.imageUrl } as never,
            { type: 'text', text: prompt },
          ],
          pageContext: {
            attachedImage: {
              url: payload.imageUrl,
              originalPrompt: payload.prompt,
            },
          },
        });
        onClose();
        navigate(route);
      } catch (err) {
        toast.error('Could not start the edit. Please try again.');
        if (import.meta.env.DEV) console.error('[ImageBody] handoffToChat failed:', err);
      }
    });
    queueMicrotask(() => {
      isHandingOffRef.current = false;
    });
    return true;
  };

  const handleVariations = () => {
    handoffToChat(payload.prompt ?? 'Try another version of this image', 'variations');
  };

  // ── Edit persistence (editor-QA P0, 2026-07-03) ──────────────────────────
  // Every edit used to live only in dialog state: AI iterations DO get a new
  // media row server-side but the Library cache was never invalidated, and
  // crop/rotate/flip uploaded a storage file no row ever pointed at. The
  // Library therefore always showed the pre-edit image.
  /** Repoint this asset's media row at the transformed file + refresh lists. */
  const persistTransformUrl = async (publicUrl: string): Promise<void> => {
    return persistImageTransform({
      assetId: payload.assetId,
      publicUrl,
      queryClient,
      onPersisted: () => {
        transformPersistedRef.current = true;
      },
    });
  };

  const persistTransformBlob = async (
    blob: Blob,
    filename: string,
    previousUrl: string,
  ): Promise<void> => {
    await persistImageTransformBlob({
      assetId: payload.assetId,
      blob,
      filename,
      previousUrl,
      queryClient,
      setImageUrl: (imageUrl, options) => {
        useImageDialogStore.getState().setImageUrl(imageUrl, options);
      },
      onPersisted: () => {
        transformPersistedRef.current = true;
      },
    });
  };

  const invalidateMediaQueries = () => {
    void queryClient.invalidateQueries({ queryKey: ['library'] });
    void queryClient.invalidateQueries({ queryKey: ['media-library'] });
    void queryClient.invalidateQueries({ queryKey: ['media'] });
  };

  const handleUndo = () => {
    const store = useImageDialogStore.getState();
    if (store.history.length === 0) return;
    const reverted = store.revertToPrevious();
    if (!reverted) return;
    toast.success('Undone.');
    const nowVisible = useImageDialogStore.getState().payload?.imageUrl;
    if (transformPersistedRef.current && nowVisible && !nowVisible.startsWith('blob:')) {
      void persistTransformUrl(nowVisible);
    }
  };

  const handleRedo = () => {
    const store = useImageDialogStore.getState();
    if (store.future.length === 0) return;
    const next = store.redo();
    if (!next) return;
    toast.success('Redone.');
    if (transformPersistedRef.current && !next.startsWith('blob:')) {
      void persistTransformUrl(next);
    }
  };
  undoRef.current = handleUndo;
  redoRef.current = handleRedo;

  /**
   * Reference-Aware Media OS Wave 5 — Inspector iteration UX now routes
   * through the unified `/api/generate-image` v7 server route in edit mode
   * (Nano Banana 2 multimodal edit). Replaces the legacy
   * `generate-image-variation` Supabase edge function. Result swaps into
   * the Inspector WITHOUT closing the dialog. Prior URL is auto-pushed to
   * the undo stack by setImageUrl. Iteration runs are exclusive via the
   * operation lock.
   */
  const iterateInPlace = async (
    prompt: string,
    editType: 'inpainting' | 'background-replacement' | 'style_transfer' | 'variation' | 'recolor',
  ) => {
    const trimmed = prompt.trim();
    if (!trimmed) return;
    const token = session?.access_token;
    if (!token) {
      toast.error('Please sign in to continue.');
      return;
    }
    const store = useImageDialogStore.getState();
    if (store.pendingIteration) return;
    store.setPendingIteration(true);
    const toastId = `iterate-${payload.assetId}`;
    toast.loading('Iterating…', { id: toastId });
    try {
      const aspectRatio =
        payload.aspectRatio ||
        (payload.width && payload.height
          ? computeAspectFromDims(payload.width, payload.height)
          : '1:1');
      const editMode =
        editType === 'background-replacement'
          ? 'background-replace'
          : editType === 'recolor'
            ? 'recolor'
            : 'inpaint';
      const response = await fetch('/api/generate-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt: trimmed,
          editMode,
          referenceImageUrl: payload.imageUrl,
          aspectRatio,
          imageModel: 'auto',
        }),
      });
      if (!response.ok) {
        const err = (await response.json().catch(() => ({}))) as {
          error?: string;
          errorCode?: string;
        };
        const e = new Error(err.error || `Iteration failed (${response.status})`) as Error & {
          errorCode?: string;
        };
        if (err.errorCode) e.errorCode = err.errorCode;
        throw e;
      }
      const result = await response.json();
      if (result?.state === 'error') {
        const e = new Error(result.error || 'Iteration failed.') as Error & {
          errorCode?: string;
        };
        if (result.errorCode) e.errorCode = result.errorCode;
        throw e;
      }
      const imageUrl = result.imageUrl || result.image_url;
      if (!imageUrl) throw new Error('No image returned.');
      useImageDialogStore.getState().setImageUrl(imageUrl);
      // The route already inserted a media row for the edited image — refresh
      // the Library so it actually appears (it was cache-stale before).
      invalidateMediaQueries();
      toast.success('Updated. Tap Undo to revert.', {
        id: toastId,
        action: {
          label: 'Undo',
          onClick: () => {
            undoRef.current();
          },
        },
      });
    } catch (err) {
      // Never surface a raw provider error to the user — log it for debugging and
      // toast a friendly, categorized message (the server sends a structured code).
      console.error(
        '[ImageBody] iterate-in-place failed:',
        err instanceof Error ? err.message : String(err),
      );
      toast.error(friendlyMessageFromError(err), { id: toastId });
    } finally {
      useImageDialogStore.getState().setPendingIteration(false);
    }
  };

  const handleCustomPrompt = (prompt: string) => {
    if (!prompt.trim()) return;
    void iterateInPlace(prompt.trim(), 'inpainting');
  };

  const handleChipAction = (chip: SuggestionChip) => {
    if (chip.namedAction === 'push-shopify') {
      trackProductEvent('push_to_shopify_intent', { origin: 'image-chip' });
      setShopifySheetOrigin('image-chip');
      return;
    }

    return op.runWithId(chip.id, async () => {
      if (chip.namedAction === 'animate') {
        const token = session?.access_token;
        if (!token) {
          toast.error('Not signed in.');
          return;
        }
        const source = payload.source ?? { kind: 'chat' as const, sessionId: 'unknown' };
        if (source.kind === 'brand-kit') {
          toast.error('Brand-kit edits not supported yet.');
          return;
        }
        const { route, sessionId } = routeForSource(source);

        // §6.6 confirm-cost gate: stash the full animate run; the dialog
        // fires it on explicit confirm. Never one-tap Veo spend.
        const runAnimate = async () => {
          const sourceMessageId = crypto.randomUUID();
          // Pass 7 — UGC-realism animation prompt baked into Animate-this from
          // the user's reference workflow (docs/prompts/HIGGSFIELD_*). Subtle
          // motion only; the goal is realism, not animation. Keeps the subject
          // mostly still — like a real person holding their phone — with tiny
          // natural micro-movements (breathing, eye flicks, minimal blinking,
          // barely noticeable handheld feel). NO dramatic motion, NO face
          // change, NO lighting change, NO camera move.
          const UGC_ANIMATE_VEO3_PROMPT = [
            'Animate this image with extremely subtle, realistic motion only.',
            'The subject remains mostly still, like a real person holding a phone close to',
            'their face. Add tiny natural movements: slight breathing, very subtle eye',
            'movement, minimal blinking, tiny changes in facial muscles, and a barely',
            'noticeable handheld camera feel.',
            'Do NOT add dramatic motion. Do NOT change the face. Do NOT change the lighting.',
            'Do NOT change the camera angle. Do NOT exaggerate expressions. Do NOT make it',
            'look animated. The goal is a realistic UGC-style AI video where the image',
            'feels alive but still natural.',
          ].join(' ');
          const animatePrompt = payload.prompt
            ? `${UGC_ANIMATE_VEO3_PROMPT}\n\nSource subject context: ${payload.prompt}`
            : UGC_ANIMATE_VEO3_PROMPT;
          toast.message('Queuing video…', { id: 'animate-video' });
          try {
            const resp = await fetch('/api/generate-video', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'Idempotency-Key': crypto.randomUUID(),
              },
              body: JSON.stringify({
                entryKind: 'image',
                sourceImageUrl: payload.imageUrl,
                prompt: animatePrompt,
                duration: 4,
                // Veo 3 generates synced audio; Animate-this was the only video
                // surface dropping it (the wizard + Video Studio both send it), so
                // animated clips came out silent. The source image IS the visual
                // reference for this image-to-video path (no anchors needed here).
                audio: true,
                aspectRatio:
                  aspect === '1:1' || aspect === '4:5' || aspect === '16:9' ? aspect : '9:16',
                sourceSessionId: sessionId,
                sourceMessageId,
              }),
            });
            if (!resp.ok) throw new Error(await resp.text());
            await resp.json();

            toast.success('Video queued — result will land in chat.', { id: 'animate-video' });
            // Pass 12 P1 fix: previously we shipped the full UGC-realism Veo
            // prompt (~80 words of "do not change face / breathing / eye
            // flicks / etc.") as the user-visible chat bubble. Sarah opened
            // the chat and saw a wall of engineering directives instead of
            // her own intent. The Veo job already fired with the full prompt
            // server-side via the /api/generate-video call above, so the
            // chat bubble only needs a short user-facing label. Engineering
            // context is still in the video job request payload above.
            useChatHandoffStore.getState().push({
              sessionId,
              parts: [
                { type: 'file', mediaType: 'image/png', url: payload.imageUrl } as never,
                { type: 'text', text: 'Animate this image' },
              ],
              pageContext: {
                attachedImage: {
                  url: payload.imageUrl,
                  originalPrompt: payload.prompt,
                },
              },
            });
            onClose();
            navigate(route);
          } catch {
            toast.error('Failed to animate image.', { id: 'animate-video' });
          }
        };

        pendingAnimateRef.current = runAnimate;
        setConfirmAnimateOpen(true);
        return;
      }

      if (chip.namedAction === 'remix-background') {
        // Defensive: the chip's prompt is defined at its definition site, but a
        // missing one must not throw inside iterateInPlace('...'.trim()).
        await iterateInPlace(
          chip.prompt ?? 'Replace only the background — keep the subject identical.',
          'background-replacement',
        );
        return;
      }

      // "Write a caption" — the agent already owns caption writing, so hand the
      // image off to chat with the intent rather than inlining a fetch here.
      // (Was falling through to a dead "coming soon" toast.)
      if (chip.namedAction === 'caption') {
        handoffToChat('Write a caption for this image', 'caption');
        return;
      }

      if (chip.action === 'auto-send' && chip.prompt) {
        // The recolor chip has a dedicated server editMode ('recolor') that its
        // generic inpaint path never reached; route it there for a truer recolor.
        const editType = chip.id === 'recolor' ? 'recolor' : 'inpainting';
        await iterateInPlace(chip.prompt, editType);
        return;
      }

      toast.message(`${chip.label} coming soon`);
    });
  };

  const handlePushToShopifyAction = () => {
    trackProductEvent('push_to_shopify_intent', { origin: 'image-action-bar' });
    setShopifySheetOrigin('image-action-bar');
  };

  /**
   * Rotate the current image 90° clockwise. Same OffscreenCanvas + 2K cap +
   * optimistic-UI pipeline as crop save — see handleCropSave for the
   * full architecture rationale. Loads the image with anonymous CORS so
   * canvas.toBlob can export it (Supabase storage allows *).
   */
  const handleRotate90 = async (): Promise<void> => {
    if (!session?.user?.id) {
      toast.error('Not signed in.');
      return;
    }
    const previousUrl = useImageDialogStore.getState().payload?.imageUrl ?? '';
    if (!previousUrl) return;
    toast.message('Rotating…', { id: 'rotate' });
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.crossOrigin = 'anonymous';
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('Image failed to load'));
        el.src = previousUrl;
      });
      // Source dims swap on a 90° rotation.
      const sourceW = img.naturalWidth;
      const sourceH = img.naturalHeight;
      const MAX_LONGEST = 2048;
      const longest = Math.max(sourceW, sourceH);
      const downscale = longest > MAX_LONGEST ? MAX_LONGEST / longest : 1;
      const targetW = Math.max(1, Math.round(sourceH * downscale)); // width = old height
      const targetH = Math.max(1, Math.round(sourceW * downscale)); // height = old width
      const useOffscreen = typeof OffscreenCanvas !== 'undefined';
      const canvas = useOffscreen
        ? new OffscreenCanvas(targetW, targetH)
        : Object.assign(document.createElement('canvas'), { width: targetW, height: targetH });
      if (!useOffscreen) {
        (canvas as HTMLCanvasElement).width = targetW;
        (canvas as HTMLCanvasElement).height = targetH;
      }
      const ctx = canvas.getContext('2d') as
        OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null;
      if (!ctx) throw new Error('Canvas 2d context unavailable');
      if ('imageSmoothingQuality' in ctx) {
        (ctx as CanvasRenderingContext2D).imageSmoothingQuality = 'high';
      }
      // Translate to the rotation pivot, rotate 90° CW, draw with the source
      // dims downscaled to the target size. drawImage's destination box is
      // expressed in the post-rotation coordinate space.
      ctx.translate(targetW, 0);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(img, 0, 0, sourceW, sourceH, 0, 0, targetH, targetW);
      const blob = useOffscreen
        ? await (canvas as OffscreenCanvas).convertToBlob({ type: 'image/webp', quality: 0.85 })
        : await new Promise<Blob | null>((resolve) =>
            (canvas as HTMLCanvasElement).toBlob((b) => resolve(b), 'image/webp', 0.85),
          );
      if (!blob) throw new Error('toBlob returned null');
      const userId = session.user.id;
      const filename = `${userId}/rotated/${payload.assetId}-${Date.now()}.webp`;
      await persistTransformBlob(blob, filename, previousUrl);
      toast.success('Rotated.', { id: 'rotate' });
    } catch (err) {
      useImageDialogStore.getState().setImageUrl(previousUrl);
      toast.error(`Rotate failed: ${err instanceof Error ? err.message : 'unknown'}`, {
        id: 'rotate',
      });
    }
  };

  /**
   * Flip the current image horizontally (mirror). Same pipeline as handleRotate90:
   * OffscreenCanvas + 2K longest-edge cap + WebP 0.85 + optimistic UI swap.
   * The transform is `translate(width, 0) + scale(-1, 1)` — flips the x-axis.
   */
  const handleFlipHorizontal = async (): Promise<void> => {
    if (!session?.user?.id) {
      toast.error('Not signed in.');
      return;
    }
    const previousUrl = useImageDialogStore.getState().payload?.imageUrl ?? '';
    if (!previousUrl) return;
    toast.message('Flipping…', { id: 'flip' });
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.crossOrigin = 'anonymous';
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('Image failed to load'));
        el.src = previousUrl;
      });
      const sourceW = img.naturalWidth;
      const sourceH = img.naturalHeight;
      const MAX_LONGEST = 2048;
      const longest = Math.max(sourceW, sourceH);
      const downscale = longest > MAX_LONGEST ? MAX_LONGEST / longest : 1;
      const targetW = Math.max(1, Math.round(sourceW * downscale));
      const targetH = Math.max(1, Math.round(sourceH * downscale));
      const useOffscreen = typeof OffscreenCanvas !== 'undefined';
      const canvas = useOffscreen
        ? new OffscreenCanvas(targetW, targetH)
        : Object.assign(document.createElement('canvas'), { width: targetW, height: targetH });
      if (!useOffscreen) {
        (canvas as HTMLCanvasElement).width = targetW;
        (canvas as HTMLCanvasElement).height = targetH;
      }
      const ctx = canvas.getContext('2d') as
        OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null;
      if (!ctx) throw new Error('Canvas 2d context unavailable');
      if ('imageSmoothingQuality' in ctx) {
        (ctx as CanvasRenderingContext2D).imageSmoothingQuality = 'high';
      }
      ctx.translate(targetW, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(img, 0, 0, sourceW, sourceH, 0, 0, targetW, targetH);
      const blob = useOffscreen
        ? await (canvas as OffscreenCanvas).convertToBlob({ type: 'image/webp', quality: 0.85 })
        : await new Promise<Blob | null>((resolve) =>
            (canvas as HTMLCanvasElement).toBlob((b) => resolve(b), 'image/webp', 0.85),
          );
      if (!blob) throw new Error('toBlob returned null');
      const userId = session.user.id;
      const filename = `${userId}/flipped/${payload.assetId}-${Date.now()}.webp`;
      await persistTransformBlob(blob, filename, previousUrl);
      toast.success('Flipped.', { id: 'flip' });
    } catch (err) {
      useImageDialogStore.getState().setImageUrl(previousUrl);
      toast.error(`Flip failed: ${err instanceof Error ? err.message : 'unknown'}`, {
        id: 'flip',
      });
    }
  };

  /**
   * Flip the current image vertically. Same pipeline as flip horizontal: only
   * the canvas transform differs — `translate(0, height) + scale(1, -1)`.
   */
  const handleFlipVertical = async (): Promise<void> => {
    if (!session?.user?.id) {
      toast.error('Not signed in.');
      return;
    }
    const previousUrl = useImageDialogStore.getState().payload?.imageUrl ?? '';
    if (!previousUrl) return;
    toast.message('Flipping…', { id: 'flip-v' });
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.crossOrigin = 'anonymous';
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('Image failed to load'));
        el.src = previousUrl;
      });
      const sourceW = img.naturalWidth;
      const sourceH = img.naturalHeight;
      const MAX_LONGEST = 2048;
      const longest = Math.max(sourceW, sourceH);
      const downscale = longest > MAX_LONGEST ? MAX_LONGEST / longest : 1;
      const targetW = Math.max(1, Math.round(sourceW * downscale));
      const targetH = Math.max(1, Math.round(sourceH * downscale));
      const useOffscreen = typeof OffscreenCanvas !== 'undefined';
      const canvas = useOffscreen
        ? new OffscreenCanvas(targetW, targetH)
        : Object.assign(document.createElement('canvas'), { width: targetW, height: targetH });
      if (!useOffscreen) {
        (canvas as HTMLCanvasElement).width = targetW;
        (canvas as HTMLCanvasElement).height = targetH;
      }
      const ctx = canvas.getContext('2d') as
        OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null;
      if (!ctx) throw new Error('Canvas 2d context unavailable');
      if ('imageSmoothingQuality' in ctx) {
        (ctx as CanvasRenderingContext2D).imageSmoothingQuality = 'high';
      }
      ctx.translate(0, targetH);
      ctx.scale(1, -1);
      ctx.drawImage(img, 0, 0, sourceW, sourceH, 0, 0, targetW, targetH);
      const blob = useOffscreen
        ? await (canvas as OffscreenCanvas).convertToBlob({ type: 'image/webp', quality: 0.85 })
        : await new Promise<Blob | null>((resolve) =>
            (canvas as HTMLCanvasElement).toBlob((b) => resolve(b), 'image/webp', 0.85),
          );
      if (!blob) throw new Error('toBlob returned null');
      const userId = session.user.id;
      const filename = `${userId}/flipped-v/${payload.assetId}-${Date.now()}.webp`;
      await persistTransformBlob(blob, filename, previousUrl);
      toast.success('Flipped.', { id: 'flip-v' });
    } catch (err) {
      useImageDialogStore.getState().setImageUrl(previousUrl);
      toast.error(`Flip failed: ${err instanceof Error ? err.message : 'unknown'}`, {
        id: 'flip-v',
      });
    }
  };

  const handleCropSave = async (blob: Blob): Promise<void> => {
    if (!session?.user?.id) {
      toast.error('Not signed in.');
      return;
    }
    // Optimistic UI: swap the dialog image to a local blob URL IMMEDIATELY so
    // the user sees their crop right away. The Supabase upload then happens
    // in the background; we swap to the persisted public URL when it lands.
    // If the upload fails, we revert to the original URL and toast an error.
    // This matches how Photoshop Express on web, Cloudinary, and Figma all
    // handle in-app crop — the user never waits on the network for their own
    // edit to appear.
    const previousUrl = useImageDialogStore.getState().payload?.imageUrl ?? '';
    const userId = session.user.id;
    const filename = `${userId}/cropped/${payload.assetId}-${Date.now()}.webp`;
    toast.message('Uploading…', { id: 'crop-save' });
    try {
      await persistTransformBlob(blob, filename, previousUrl);
      toast.success('Crop saved.', { id: 'crop-save' });
    } catch (err) {
      useImageDialogStore.getState().setImageUrl(previousUrl);
      toast.error(`Crop failed: ${err instanceof Error ? err.message : 'unknown'}`, {
        id: 'crop-save',
      });
    }
  };

  const title = resolveDisplayTitle(payload);

  // Reuse context (Task 2, 2026-08-15; relocated to a footer line 2026-08-15
  // — see design-polish pass): quiet, ambient — never a modal or a blocking
  // confirm. Noise on the common case (unused or single-use asset) is worse
  // than nothing, so this renders NOTHING unless the asset is genuinely
  // reused (>1 post). The wording escalates, not the presence, when the
  // asset also carries overlay text — that combination is the one that
  // silently ships a baked-in headline to posts nobody edited.
  //
  // Rendered as a sibling BELOW `ImageStudioDialog`, not above it: this note
  // used to sit before the dialog in the tree, which put it above the page
  // header `ImageStudioDialog` renders internally (measured live: note at
  // y≈16, header's Back/Assets breadcrumb at y≈57) — a floating band on top
  // of the app's own page header, pushing it down and reading as a detached
  // stray (docs/planning/editor-unification-screenshots/
  // P2-asset-reuse-note-desktop.png). `ImageStudioDialog`'s `meta` prop is a
  // typed `MediaStudioMeta` struct rendered in an Info popover — not a free
  // content slot — so the note can't ride inside the dialog's own header
  // without changing the protected create/v2 tree's types. A footer line
  // directly under the editor keeps it adjacent to where the risk is
  // actually taken (the edit composer sits at the very bottom of the same
  // dialog), without sitting above anything the app already owns.
  const postCount = reuseInfo.data?.postCount ?? 0;
  const showReuseNote = postCount > 1;
  const reuseNoteText =
    showReuseNote && reuseInfo.data?.hasOverlay
      ? `Has text on it, used in ${postCount} posts — that text will show wherever they publish.`
      : `Used in ${postCount} posts.`;

  return (
    <>
      <div className="flex h-full min-h-0 flex-col">
        <div className="min-h-0 flex-1">
          <ImageStudioDialog
            open
            onOpenChange={(next) => {
              if (!next) onClose();
            }}
            imageUrl={payload.imageUrl}
            title={title}
            {...(() => {
              const breadcrumb = toMediaBreadcrumb(breadcrumbs, title);
              return breadcrumb ? { breadcrumb } : {};
            })()}
            meta={meta}
            thumbnailUrl={payload.thumbnailUrl}
            context={context}
            inline
            onDownload={handleDownload}
            onVariations={handleVariations}
            onPushToShopify={handlePushToShopifyAction}
            onChipAction={handleChipAction}
            isOperating={op.isOperating}
            activeOperationId={op.activeId}
            onCustomPrompt={handleCustomPrompt}
            onCreatePost={handleCreatePost}
            onCreateWithMora={handleCreateWithMora}
            isCreatingPost={isCreatingManualPost}
            onCropSave={handleCropSave}
            onRotate90={handleRotate90}
            onFlipHorizontal={handleFlipHorizontal}
            onFlipVertical={handleFlipVertical}
            onUndo={handleUndo}
            onRedo={handleRedo}
            canUndo={canUndo}
            canRedo={canRedo}
            regenerable={!!payload.prompt}
            artifactId={payload.assetId}
            artifactKind="media"
          />
        </div>
        {showReuseNote ? (
          <div
            className={`flex shrink-0 items-center gap-1.5 border-t border-border/30 px-4 py-2 ${ITEM_META}`}
          >
            <StackIcon className="size-3.5 shrink-0" weight="regular" />
            <span className="truncate">{reuseNoteText}</span>
          </div>
        ) : null}
      </div>
      <ConnectShopifySheet
        open={shopifySheetOrigin !== null}
        onOpenChange={(next) => {
          if (!next) setShopifySheetOrigin(null);
        }}
        origin={shopifySheetOrigin ?? 'image-chip'}
        assetUrl={payload.imageUrl}
      />
      <ConfirmCostDialog
        open={confirmAnimateOpen}
        onOpenChange={setConfirmAnimateOpen}
        title="Make a video"
        estimate="~$1.60 for a 4-second clip"
        detail="Generation takes a few minutes — the clip lands back in chat when ready."
        confirmLabel="Generate video"
        onConfirm={() => {
          setConfirmAnimateOpen(false);
          pendingAnimateRef.current?.();
          pendingAnimateRef.current = null;
        }}
      />
    </>
  );
};
